import { toast } from "@/components/ui/use-toast";
import { CATALOG_ERROR_CODES, CatalogApiError, toCatalogApiError } from "./catalogErrors";
import { DEADLINE_CHANGE_TITLE } from "@/lib/deadlines/deadlineChangeLabels";

export interface CatalogErrorPresentation {
  title: string;
  description: string;
}

function validationMessage(error: CatalogApiError): string {
  const messages = [...new Set(error.findings.map((finding) => finding.message).filter(Boolean))];
  if (messages.length === 0) return "Some values need attention.";
  if (messages.length === 1) return messages[0]!;
  return `${messages[0]} ${messages.length - 1} more issue${messages.length === 2 ? "" : "s"} need attention.`;
}

/** One translation table for every catalog API failure the reviewer can encounter. */
export function catalogErrorPresentation(failure: unknown): CatalogErrorPresentation {
  const error = toCatalogApiError(failure);
  if (error.followupFindings.length > 0) {
    return { title: "Check the follow-up changes", description: error.followupFindings[0]!.message };
  }
  if (error.status === 422 || error.code === CATALOG_ERROR_CODES.validationFailed) {
    return { title: "Check the highlighted fields", description: validationMessage(error) };
  }
  if (error.code === CATALOG_ERROR_CODES.deadlineChangeRequiresConfirmation) {
    return { title: DEADLINE_CHANGE_TITLE, description: "Confirm the change of the due dates to save. The system saved nothing." };
  }
  if (error.code === CATALOG_ERROR_CODES.staleRevision) {
    return { title: "This case changed elsewhere", description: "Your unsaved changes are still on screen." };
  }
  if (error.code === CATALOG_ERROR_CODES.caseLocked || error.code === CATALOG_ERROR_CODES.lockNotHeld) {
    return { title: "Editing is unavailable", description: error.message || "This case is locked by another reviewer." };
  }
  if (error.status === 401) {
    return { title: "Your session has expired", description: "Sign in again, then retry the action." };
  }
  if (error.status === 403) {
    return { title: "You don’t have permission", description: "You are not allowed to perform this action." };
  }
  if (error.status === 0) {
    return { title: "Can’t reach the server", description: "Check your connection and try again." };
  }
  return { title: "Something went wrong", description: error.message || "Try the action again." };
}

/** Shows the shared destructive toast and returns the normalized error for local field handling. */
export function presentCatalogError(failure: unknown): CatalogApiError {
  const error = toCatalogApiError(failure);
  toast({ ...catalogErrorPresentation(error), variant: "destructive" });
  return error;
}
